import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ConversationAPI from 'dashboard/api/conversations';
import types from 'dashboard/store/mutation-types';

/**
 * Shared optimistic label mutation for a single, specific conversation
 * (as opposed to the bulk-actions label assignment used by multi-select).
 * Used by both the conversation card's remove ("x") button and its
 * right-click label toggle submenu, so both stay in sync and don't
 * duplicate the optimistic-update/revert/alert logic.
 */
export function useConversationLabelMutation() {
  const store = useStore();
  const { t } = useI18n();

  /**
   * @param {number} conversationId
   * @param {string[]} updatedLabels - full label title list after the change
   * @param {string[]} previousLabels - full label title list before the change (for revert)
   * @returns {Promise<boolean>} whether the update succeeded
   */
  const updateConversationLabels = async (
    conversationId,
    updatedLabels,
    previousLabels
  ) => {
    // Optimistic update: patch the conversation in the store right away so
    // the UI (card chips, menu checkmarks) reflects the change immediately,
    // without waiting for the API round trip.
    store.commit(types.UPDATE_CONVERSATION, {
      id: conversationId,
      labels: updatedLabels,
    });

    try {
      await ConversationAPI.updateLabels(conversationId, updatedLabels);
      store.dispatch('conversationLabels/setConversationLabel', {
        id: conversationId,
        data: updatedLabels,
      });
      return true;
    } catch (error) {
      // Revert on failure
      store.commit(types.UPDATE_CONVERSATION, {
        id: conversationId,
        labels: previousLabels,
      });
      useAlert(t('CONVERSATION.CARD.LABEL_UPDATE_ERROR'));
      return false;
    }
  };

  return { updateConversationLabels };
}
